/* eslint-disable no-underscore-dangle */
import { put, call, takeLatest } from 'redux-saga/effects';
import {
  GET_BLOCKLIST,
  GET_BLOCKLIST_INDEX,
  ADD_BLOCKLIST_ENTRY,
  REMOVE_BLOCKLIST_ENTRY,
  LOOKUP_BLOCKLIST_NUMBER,
  UPDATE_BLOCKLIST_SETTINGS
} from '../action/constant';
import request from '../utils/request';
import {
  getBlocklistRequest,
  getBlocklistSuccess,
  getBlocklistError,
  addBlocklistEntrySuccess,
  addBlocklistEntryError,
  removeBlocklistEntrySuccess,
  removeBlocklistEntryError,
  lookupNumberSuccess,
  lookupNumberError,
  getBlocklistIndexSuccess,
  getBlocklistIndexError,
  updateBlocklistSettingsSuccess,
  updateBlocklistSettingsError
} from '../action/blocklistAction';

/**
 * The token is read per request rather than once at module scope, so a login
 * in the same tab does not leave every blocklist call using a stale token.
 *
 * The API strips exactly 7 characters from this header. The rest of this app
 * sends 'VMS-2.0' + token, which happens to be 7 characters; the blocklist
 * routes are documented as Bearer, so they use the documented prefix.
 */
function authHeaders() {
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`
  };
}

/** buildingId is a query param on every blocklist route, including POST and PUT. */
function currentBuildingId() {
  try {
    const building = JSON.parse(localStorage.getItem('building'));
    return building && building._id;
  } catch (e) {
    return null;
  }
}

const NO_BUILDING = {
  status: 0,
  message: 'No building selected. Pick a building and try again.',
  data: null
};

/**
 * The API writes its `error` strings to be shown to the user, so they are
 * passed through verbatim. Note that this codebase's server also answers 404
 * for unexpected failures, so 404 is never read as a bare "not found" without
 * looking at the message.
 */
function toApiError(err) {
  const response = err && err.response;
  if (!response) {
    return {
      status: 0,
      message: 'Could not reach the server. Check your connection and try again.',
      data: null
    };
  }
  const data = response.data || {};
  return {
    status: response.status,
    message: data.error || 'Something went wrong. Please try again.',
    data
  };
}

/**
 * Search text is sent exactly as typed — the server escapes regex characters
 * and matches phone numbers on their digits. encodeURIComponent here is URL
 * transport encoding, not sanitising: without it a '+' in the query string
 * would arrive at the server as a space.
 */
function* getBlocklistSaga(action) {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(getBlocklistError(NO_BUILDING));
    return;
  }
  const payload = (action && action.data) || {};
  const search = encodeURIComponent(payload.search || '');
  const page = payload.page || 1;
  const limit = payload.limit || 20;
  const url = `Blocklist/list?buildingId=${buildingId}&search=${search}`
    + `&page=${page}&limit=${limit}`;
  try {
    const response = yield call(request, url, {
      method: 'GET',
      headers: authHeaders()
    });
    yield put(getBlocklistSuccess(response.data.data));
  } catch (err) {
    yield put(getBlocklistError(toApiError(err)));
  }
}

/** Optional fields are omitted rather than sent empty, so nothing is stored as ''. */
function buildAddBody(data) {
  const body = {
    phone: data.phone,
    reason: data.reason
  };
  if (data.name) body.name = data.name;
  if (data.idDocumentNumber) body.idDocumentNumber = data.idDocumentNumber;
  if (data.visitorRecordId) body.visitorRecordId = data.visitorRecordId;
  return body;
}

function* addBlocklistEntrySaga(action) {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(addBlocklistEntryError(NO_BUILDING));
    return;
  }
  const url = `Blocklist/add?buildingId=${buildingId}`;
  try {
    const response = yield call(request, url, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json'
      },
      data: buildAddBody(action.data)
    });
    yield put(addBlocklistEntrySuccess(response.data.data));
  } catch (err) {
    // A 409 carries the existing entry in `data`, so the modal can name who
    // blocked the number and when instead of showing a bare error.
    yield put(addBlocklistEntryError(toApiError(err)));
  }
}

/**
 * Remove is a soft delete and is idempotent — calling it twice answers 200
 * both times, so double clicks and retries after a timeout are safe.
 */
function* removeBlocklistEntrySaga(action) {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(removeBlocklistEntryError(NO_BUILDING));
    return;
  }
  const url = `Blocklist/remove?buildingId=${buildingId}`;
  try {
    const response = yield call(request, url, {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json'
      },
      data: { entryId: action.data.entryId }
    });
    yield put(removeBlocklistEntrySuccess(response.data.data));
    if (action.data.refetch) {
      yield put(getBlocklistRequest(action.data.refetch));
    }
  } catch (err) {
    yield put(removeBlocklistEntryError(toApiError(err)));
  }
}

/**
 * `found: false` is a normal answer, not an error — a number with no visit
 * history can still be blocked.
 */
function* lookupNumberSaga(action) {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(lookupNumberError(NO_BUILDING));
    return;
  }
  const phone = encodeURIComponent(action.data.phone || '');
  const url = `Blocklist/lookup?buildingId=${buildingId}&phone=${phone}`;
  try {
    const response = yield call(request, url, {
      method: 'GET',
      headers: authHeaders()
    });
    yield put(lookupNumberSuccess({
      ...response.data.data,
      phone: action.data.phone
    }));
  } catch (err) {
    yield put(lookupNumberError(toApiError(err)));
  }
}

/**
 * One call for the whole active list, so the visitors table can decide Block
 * or Allow per row locally. A building's blocklist is small by nature; if one
 * ever exceeds this limit the table falls back to showing Block, which is the
 * safe direction — the API still rejects a duplicate with a 409.
 */
const INDEX_LIMIT = 500;

function* getBlocklistIndexSaga() {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(getBlocklistIndexError(NO_BUILDING));
    return;
  }
  const url = `Blocklist/list?buildingId=${buildingId}&page=1&limit=${INDEX_LIMIT}`;
  try {
    const response = yield call(request, url, {
      method: 'GET',
      headers: authHeaders()
    });
    yield put(getBlocklistIndexSuccess(response.data.data));
  } catch (err) {
    yield put(getBlocklistIndexError(toApiError(err)));
  }
}

/**
 * Building settings live behind the existing PUT /Building/update, which is
 * multipart/form-data. `blocklistNotify` therefore has to be JSON-encoded as a
 * string, the same way `purpose_Category` already is — a plain object fails to
 * parse server-side, silently.
 *
 * Only the fields the admin changed are appended: an omitted field is left
 * unchanged, so a partial write is safe.
 */
function buildSettingsForm(changes) {
  const form = new FormData();
  if (typeof changes.blocklist === 'boolean') {
    form.append('blocklist', String(changes.blocklist));
  }
  if (changes.defaultCountryCode) {
    form.append('defaultCountryCode', changes.defaultCountryCode);
  }
  if (changes.notifyEmails) {
    form.append('blocklistNotify', JSON.stringify({ emails: changes.notifyEmails }));
  }
  return form;
}

/**
 * Six sagas read the selected building straight out of localStorage, so the
 * cached copy has to be brought in line with what was just saved. Without
 * this, the enforcement banner keeps reading a stale flag until the admin
 * picks a building again.
 */
function cacheBuildingSettings(changes) {
  try {
    const building = JSON.parse(localStorage.getItem('building')) || {};
    const updated = { ...building };
    if (typeof changes.blocklist === 'boolean') updated.blocklist = changes.blocklist;
    if (changes.defaultCountryCode) updated.defaultCountryCode = changes.defaultCountryCode;
    if (changes.notifyEmails) updated.blocklistNotify = { emails: changes.notifyEmails };
    localStorage.setItem('building', JSON.stringify(updated));
  } catch (e) {
    // A cache that cannot be written is not worth failing the save over.
  }
}

function* updateSettingsSaga(action) {
  const buildingId = currentBuildingId();
  if (!buildingId) {
    yield put(updateBlocklistSettingsError(NO_BUILDING));
    return;
  }
  // NOTE: confirm the shape of this route with the backend team before relying
  // on it — it is documented in §7 of the handover but is not called anywhere
  // else in this app, so the buildingId placement is an assumption.
  const url = `Building/update?buildingId=${buildingId}`;
  try {
    // Content-Type is left to axios so the multipart boundary is set for us.
    yield call(request, url, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        Authorization: `VMS-2.0${localStorage.getItem('token')}`
      },
      data: buildSettingsForm(action.data)
    });
    cacheBuildingSettings(action.data);
    yield put(updateBlocklistSettingsSuccess(action.data));
  } catch (err) {
    yield put(updateBlocklistSettingsError(toApiError(err)));
  }
}

export default function* blocklistWatcherSaga() {
  yield takeLatest(GET_BLOCKLIST.PENDING, getBlocklistSaga);
  yield takeLatest(ADD_BLOCKLIST_ENTRY.PENDING, addBlocklistEntrySaga);
  yield takeLatest(REMOVE_BLOCKLIST_ENTRY.PENDING, removeBlocklistEntrySaga);
  yield takeLatest(LOOKUP_BLOCKLIST_NUMBER.PENDING, lookupNumberSaga);
  yield takeLatest(GET_BLOCKLIST_INDEX.PENDING, getBlocklistIndexSaga);
  yield takeLatest(UPDATE_BLOCKLIST_SETTINGS.PENDING, updateSettingsSaga);
}
